import { getNearestSegment, interpolateOnSegment, intersectRects } from './screencastGeometry';
import type { NearestSegment, RectGeometry } from './screencastGeometry';
import type { Area, ScreencastTooltips, ValueDistribution } from './types';

const HPL_TITLE = 'HPL';
const TVD_TITLE = 'TVD';
const TVDSS_TITLE = 'TVDSS';

export type LineMeasurementPoint = {
  x: number;
  y: number;
};

export type LineMeasurementVerticalAxis = 'TVD' | 'TVDSS';

export type ResolvedLineMeasurement = {
  verticalAxis: LineMeasurementVerticalAxis;
  distance: number;
  angle: number;
  deltaHpl: number;
  deltaVertical: number;
  tvdssAtEnd: number | null;
  units: {
    distance?: string;
    hpl?: string;
    vertical?: string;
    tvdss?: string;
  };
};

type AxisValues = {
  hpl: AxisValue;
  tvd: AxisValue | null;
  tvdss: AxisValue | null;
};

type AxisValue = {
  value: number;
  distribution: ValueDistribution;
};

type AxisValueCandidate = AxisValue & {
  distance: number;
};

type VerticalMeasurement = {
  axis: LineMeasurementVerticalAxis;
  startY: number;
  endY: number;
  startDistribution: ValueDistribution;
  endDistribution: ValueDistribution;
};

export function resolveLineMeasurementBetween(
  tooltips: ScreencastTooltips | null | undefined,
  start: LineMeasurementPoint,
  end: LineMeasurementPoint,
): ResolvedLineMeasurement | null {
  if (!tooltips || !isValidPoint(start) || !isValidPoint(end)) {
    return null;
  }

  const startAxisValues = resolveAxisValuesAtPoint(tooltips, start);
  const endAxisValues = resolveAxisValuesAtPoint(tooltips, end);
  if (!startAxisValues || !endAxisValues) {
    return null;
  }

  const verticalMeasurement = resolveVerticalMeasurement(startAxisValues, endAxisValues);
  if (!verticalMeasurement) {
    return null;
  }

  const deltaHpl = endAxisValues.hpl.value - startAxisValues.hpl.value;
  const deltaVertical = verticalMeasurement.endY - verticalMeasurement.startY;
  const distance = Math.hypot(deltaHpl, deltaVertical);
  const angle = getInclinationAngle(deltaHpl, deltaVertical);

  return {
    verticalAxis: verticalMeasurement.axis,
    distance,
    angle,
    deltaHpl,
    deltaVertical,
    tvdssAtEnd: endAxisValues.tvdss?.value ?? null,
    units: {
      distance: getCommonUnit(
        startAxisValues.hpl.distribution,
        endAxisValues.hpl.distribution,
        verticalMeasurement.startDistribution,
        verticalMeasurement.endDistribution,
      ),
      hpl: getCommonUnit(startAxisValues.hpl.distribution, endAxisValues.hpl.distribution),
      vertical: getCommonUnit(verticalMeasurement.startDistribution, verticalMeasurement.endDistribution),
      tvdss: getUnit(endAxisValues.tvdss?.distribution ?? null),
    },
  };
}

function resolveAxisValuesAtPoint(tooltips: ScreencastTooltips, point: LineMeasurementPoint): AxisValues | null {
  const hpl = resolveAxisValueAtPoint(tooltips, point, HPL_TITLE);
  if (!hpl) {
    return null;
  }

  return {
    hpl,
    tvd: resolveAxisValueAtPoint(tooltips, point, TVD_TITLE),
    tvdss: resolveAxisValueAtPoint(tooltips, point, TVDSS_TITLE),
  };
}

function resolveAxisValueAtPoint(
  tooltips: ScreencastTooltips,
  point: LineMeasurementPoint,
  axisTitle: string,
): AxisValue | null {
  let nearestCandidate: AxisValueCandidate | null = null;

  for (const area of tooltips.areas) {
    nearestCandidate = collectNearestAxisValueCandidate(area, point, axisTitle, null, nearestCandidate);
  }

  if (!nearestCandidate) {
    return null;
  }

  return {
    value: nearestCandidate.value,
    distribution: nearestCandidate.distribution,
  };
}

function collectNearestAxisValueCandidate(
  area: Area,
  point: LineMeasurementPoint,
  axisTitle: string,
  clipRect: RectGeometry | null,
  nearestCandidate: AxisValueCandidate | null,
): AxisValueCandidate | null {
  if (area.kind === 'rect') {
    const visibleRect = clipRect ? intersectRects(area.rect, clipRect) : area.rect;
    if (!visibleRect || !containsRectPoint(visibleRect, point)) {
      return nearestCandidate;
    }

    let nextNearestCandidate = nearestCandidate;
    for (const childArea of area.areas) {
      nextNearestCandidate = collectNearestAxisValueCandidate(
        childArea,
        point,
        axisTitle,
        visibleRect,
        nextNearestCandidate,
      );
    }
    return nextNearestCandidate;
  }

  let nextNearestCandidate = nearestCandidate;
  const distribution = findDistribution(area.values, axisTitle);
  if (distribution) {
    const nearestSegment = getNearestSegment(area.segments, point.x, point.y, clipRect);
    if (nearestSegment && nearestSegment.distance <= area.radius) {
      const axisValue = getAxisValue(distribution, nearestSegment, point);
      if (axisValue && (!nextNearestCandidate || nearestSegment.distance < nextNearestCandidate.distance)) {
        nextNearestCandidate = {
          ...axisValue,
          distance: nearestSegment.distance,
        };
      }
    }
  }

  for (const childArea of area.areas) {
    nextNearestCandidate = collectNearestAxisValueCandidate(
      childArea,
      point,
      axisTitle,
      clipRect,
      nextNearestCandidate,
    );
  }

  return nextNearestCandidate;
}

function getAxisValue(
  distribution: ValueDistribution | null,
  nearestSegment: NearestSegment,
  point: LineMeasurementPoint,
): AxisValue | null {
  if (!distribution?.canInterpolate) {
    return null;
  }

  const value = interpolateOnSegment(nearestSegment, distribution.values, point.x, point.y);

  if (!hasNumber(value)) {
    return null;
  }

  return {
    value,
    distribution,
  };
}

function resolveVerticalMeasurement(startValues: AxisValues, endValues: AxisValues): VerticalMeasurement | null {
  if (startValues.tvd && endValues.tvd) {
    return {
      axis: 'TVD',
      startY: startValues.tvd.value,
      endY: endValues.tvd.value,
      startDistribution: startValues.tvd.distribution,
      endDistribution: endValues.tvd.distribution,
    };
  }

  if (startValues.tvdss && endValues.tvdss) {
    return {
      axis: 'TVDSS',
      startY: startValues.tvdss.value,
      endY: endValues.tvdss.value,
      startDistribution: startValues.tvdss.distribution,
      endDistribution: endValues.tvdss.distribution,
    };
  }

  return null;
}

function findDistribution(distributions: ValueDistribution[], title: string): ValueDistribution | null {
  return (
    distributions.find((distribution) => hasAxisTitle(distribution.title, title) && distribution.canInterpolate) ?? null
  );
}

function containsRectPoint(rect: RectGeometry, point: LineMeasurementPoint): boolean {
  const [xmin, ymin, xmax, ymax] = rect;
  return point.x >= xmin && point.x <= xmax && point.y >= ymin && point.y <= ymax;
}

function isValidPoint(point: LineMeasurementPoint): boolean {
  return Number.isFinite(point.x) && Number.isFinite(point.y);
}

function hasNumber(value: number | null): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function normalizeTitle(title: string | undefined): string {
  return title?.trim().toUpperCase() ?? '';
}

function hasAxisTitle(title: string | undefined, axisTitle: string): boolean {
  const normalizedTitle = normalizeTitle(title);
  if (normalizedTitle === axisTitle) {
    return true;
  }

  return normalizedTitle.split(/[^A-Z0-9]+/).includes(axisTitle);
}

function normalizeAngle(value: number): number {
  if (Object.is(value, -0)) {
    return 0;
  }

  return Math.abs(value - 180) < Number.EPSILON ? -180 : value;
}

function getInclinationAngle(deltaHpl: number, deltaVertical: number): number {
  return normalizeAngle((Math.atan2(deltaVertical, deltaHpl) * 180) / Math.PI);
}

function getUnit(distribution: ValueDistribution | null): string | undefined {
  return distribution?.unit?.trim() || undefined;
}

function getCommonUnit(...distributions: ValueDistribution[]): string | undefined {
  const units = distributions.map(getUnit);
  const [firstUnit] = units;

  if (!firstUnit || units.some((unit) => unit !== firstUnit)) {
    return undefined;
  }

  return firstUnit;
}
